/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { WalletTypeEnum } from './WalletTypeEnum';
import {
    WalletTypeEnumFromJSON,
    WalletTypeEnumFromJSONTyped,
    WalletTypeEnumToJSON,
} from './WalletTypeEnum';
import type { InvestmentVoucherWithDisagio } from './InvestmentVoucherWithDisagio';
import {
    InvestmentVoucherWithDisagioFromJSON,
    InvestmentVoucherWithDisagioFromJSONTyped,
    InvestmentVoucherWithDisagioToJSON,
} from './InvestmentVoucherWithDisagio';
import type { SurveyWaiverTypeEnum } from './SurveyWaiverTypeEnum';
import {
    SurveyWaiverTypeEnumFromJSON,
    SurveyWaiverTypeEnumFromJSONTyped,
    SurveyWaiverTypeEnumToJSON,
} from './SurveyWaiverTypeEnum';
import type { Wallet } from './Wallet';
import {
    WalletFromJSON,
    WalletFromJSONTyped,
    WalletToJSON,
} from './Wallet';
import type { MinimalInvestor } from './MinimalInvestor';
import {
    MinimalInvestorFromJSON,
    MinimalInvestorFromJSONTyped,
    MinimalInvestorToJSON,
} from './MinimalInvestor';
import type { Money } from './Money';
import {
    MoneyFromJSON,
    MoneyFromJSONTyped,
    MoneyToJSON,
} from './Money';
import type { StepEnum } from './StepEnum';
import {
    StepEnumFromJSON,
    StepEnumFromJSONTyped,
    StepEnumToJSON,
} from './StepEnum';
import type { InvestmentCancellationEnum } from './InvestmentCancellationEnum';
import {
    InvestmentCancellationEnumFromJSON,
    InvestmentCancellationEnumFromJSONTyped,
    InvestmentCancellationEnumToJSON,
} from './InvestmentCancellationEnum';
import type { KycStatusEnum } from './KycStatusEnum';
import {
    KycStatusEnumFromJSON,
    KycStatusEnumFromJSONTyped,
    KycStatusEnumToJSON,
} from './KycStatusEnum';
import type { FrontendStatusEnum } from './FrontendStatusEnum';
import {
    FrontendStatusEnumFromJSON,
    FrontendStatusEnumFromJSONTyped,
    FrontendStatusEnumToJSON,
} from './FrontendStatusEnum';
import type { InvestmentTokenForInvestor } from './InvestmentTokenForInvestor';
import {
    InvestmentTokenForInvestorFromJSON,
    InvestmentTokenForInvestorFromJSONTyped,
    InvestmentTokenForInvestorToJSON,
} from './InvestmentTokenForInvestor';
import type { InvestmentAcquisitionTypeRecipientEnum } from './InvestmentAcquisitionTypeRecipientEnum';
import {
    InvestmentAcquisitionTypeRecipientEnumFromJSON,
    InvestmentAcquisitionTypeRecipientEnumFromJSONTyped,
    InvestmentAcquisitionTypeRecipientEnumToJSON,
} from './InvestmentAcquisitionTypeRecipientEnum';
import type { TokenizationApiOperationStatus } from './TokenizationApiOperationStatus';
import {
    TokenizationApiOperationStatusFromJSON,
    TokenizationApiOperationStatusFromJSONTyped,
    TokenizationApiOperationStatusToJSON,
} from './TokenizationApiOperationStatus';

/**
 * 
 * @export
 * @interface InvestmentDetails
 */
export interface InvestmentDetails {
    /**
     * 
     * @type {string}
     * @memberof InvestmentDetails
     */
    readonly id: string;
    /**
     * the date the issuer accepted the investment offer.
     * @type {Date}
     * @memberof InvestmentDetails
     */
    acceptedAt?: Date | null;
    /**
     * 
     * @type {Money}
     * @memberof InvestmentDetails
     */
    readonly accruedInterest: Money;
    /**
     * 
     * @type {InvestmentAcquisitionTypeRecipientEnum}
     * @memberof InvestmentDetails
     */
    acquisitionType?: InvestmentAcquisitionTypeRecipientEnum;
    /**
     * 
     * @type {WalletTypeEnum}
     * @memberof InvestmentDetails
     */
    addWalletLater: WalletTypeEnum | null;
    /**
     * 
     * @type {Money}
     * @memberof InvestmentDetails
     */
    readonly agio: Money | null;
    /**
     * This is only information if the Investment can be aborted in principle. One still needs to check if the requesting user has permission to do so.
     * @type {boolean}
     * @memberof InvestmentDetails
     */
    readonly canAbort: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof InvestmentDetails
     */
    readonly canAccept: boolean;
    /**
     * This is only information if the Investment can be cancelled in principle. One still needs to check if the requesting user has permission to do so.
     * @type {Array<InvestmentCancellationEnum>}
     * @memberof InvestmentDetails
     */
    readonly canCancel: Array<InvestmentCancellationEnum>;
    /**
     * This is only information if the Investment can be issued in principle. One still needs to check if the requesting user has permission to do so.
     * @type {boolean}
     * @memberof InvestmentDetails
     */
    readonly canIssue: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof InvestmentDetails
     */
    readonly canReceivePayment: boolean;
    /**
     * the date the issuer, admin or investor canceled the investment offer
     * @type {Date}
     * @memberof InvestmentDetails
     */
    cancellationDate?: Date | null;
    /**
     * 
     * @type {Date}
     * @memberof InvestmentDetails
     */
    readonly cancellationDeadline: Date | null;
    /**
     * reason why investment was canceled / aborted
     * @type {InvestmentCancellationEnum}
     * @memberof InvestmentDetails
     */
    cancellationReason?: InvestmentCancellationEnum | null;
    /**
     * 
     * @type {Date}
     * @memberof InvestmentDetails
     */
    readonly considerationStartedAt: Date;
    /**
     * 
     * @type {Money}
     * @memberof InvestmentDetails
     */
    readonly disagio: Money | null;
    /**
     * 
     * @type {FrontendStatusEnum}
     * @memberof InvestmentDetails
     */
    readonly frontendStatus: FrontendStatusEnum;
    /**
     * 
     * @type {number}
     * @memberof InvestmentDetails
     */
    fulfilledAmount: number;
    /**
     * whether an identification was created by this investment
     * @type {boolean}
     * @memberof InvestmentDetails
     */
    initiatedIdentification?: boolean;
    /**
     * 
     * @type {Money}
     * @memberof InvestmentDetails
     */
    readonly investmentTotalFulfilled: Money;
    /**
     * 
     * @type {Money}
     * @memberof InvestmentDetails
     */
    readonly investmentTotal: Money;
    /**
     * 
     * @type {MinimalInvestor}
     * @memberof InvestmentDetails
     */
    investor: MinimalInvestor;
    /**
     * 
     * @type {boolean}
     * @memberof InvestmentDetails
     */
    readonly isEcsp: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof InvestmentDetails
     */
    readonly isTokenized: boolean;
    /**
     * 
     * @type {TokenizationApiOperationStatus}
     * @memberof InvestmentDetails
     */
    readonly issuanceStatus: TokenizationApiOperationStatus | null;
    /**
     * 
     * @type {KycStatusEnum}
     * @memberof InvestmentDetails
     */
    readonly kycStatus: KycStatusEnum | null;
    /**
     * 
     * @type {Money}
     * @memberof InvestmentDetails
     */
    readonly lastSettledPayout: Money | null;
    /**
     * 
     * @type {number}
     * @memberof InvestmentDetails
     */
    maxAmount: number;
    /**
     * 
     * @type {Date}
     * @memberof InvestmentDetails
     */
    readonly nextScheduledPayout: Date | null;
    /**
     * 
     * @type {Date}
     * @memberof InvestmentDetails
     */
    paidAt?: Date | null;
    /**
     * bank transfer text for associating the payment
     * @type {string}
     * @memberof InvestmentDetails
     */
    readonly paymentCode: string;
    /**
     * 
     * @type {Date}
     * @memberof InvestmentDetails
     */
    readonly paymentDeadline: Date | null;
    /**
     * 
     * @type {Money}
     * @memberof InvestmentDetails
     */
    readonly paymentTotal: Money;
    /**
     * 
     * @type {string}
     * @memberof InvestmentDetails
     */
    productName: string;
    /**
     * 
     * @type {boolean}
     * @memberof InvestmentDetails
     */
    readonly redeemed: boolean;
    /**
     * 
     * @type {number}
     * @memberof InvestmentDetails
     */
    remainingAmount: number;
    /**
     * the date the investor made the investment offer.
     * @type {Date}
     * @memberof InvestmentDetails
     */
    signedAt: Date;
    /**
     * 
     * @type {StepEnum}
     * @memberof InvestmentDetails
     */
    step?: StepEnum;
    /**
     * 
     * @type {Money}
     * @memberof InvestmentDetails
     */
    readonly sumSettledPayouts: Money;
    /**
     * survey was valid at submission time and thus no waiver is required
     * @type {boolean}
     * @memberof InvestmentDetails
     */
    surveyValid?: boolean;
    /**
     * 
     * @type {SurveyWaiverTypeEnum}
     * @memberof InvestmentDetails
     */
    surveyWaiverType?: SurveyWaiverTypeEnum;
    /**
     * 
     * @type {Date}
     * @memberof InvestmentDetails
     */
    tokenDeliveredAt?: Date | null;
    /**
     * 
     * @type {Money}
     * @memberof InvestmentDetails
     */
    readonly tokenPrice: Money;
    /**
     * transaction hash in the blockchain. tokens are considered issued once they are on the blockchain.
     * @type {string}
     * @memberof InvestmentDetails
     */
    tokenTransactionHash?: string;
    /**
     * 
     * @type {InvestmentTokenForInvestor}
     * @memberof InvestmentDetails
     */
    readonly token: InvestmentTokenForInvestor;
    /**
     * 
     * @type {boolean}
     * @memberof InvestmentDetails
     */
    readonly usesEffecta: boolean;
    /**
     * 
     * @type {InvestmentVoucherWithDisagio}
     * @memberof InvestmentDetails
     */
    readonly voucher: InvestmentVoucherWithDisagio | null;
    /**
     * 
     * @type {Wallet}
     * @memberof InvestmentDetails
     */
    wallet: Wallet | null;
    /**
     * 
     * @type {Date}
     * @memberof InvestmentDetails
     */
    readonly createdAt: Date;
    /**
     * 
     * @type {Money}
     * @memberof InvestmentDetails
     */
    exemptionOrderAmount: Money | null;
    /**
     * Latest time when the exemption_order_amount was changed
     * @type {Date}
     * @memberof InvestmentDetails
     */
    exemptionOrderDate?: Date | null;
}



/**
 * Check if a given object implements the InvestmentDetails interface.
 */
export function instanceOfInvestmentDetails(value: object): value is InvestmentDetails {
    if (!('id' in value) || value['id'] === undefined) return false;
    if (!('accruedInterest' in value) || value['accruedInterest'] === undefined) return false;
    if (!('addWalletLater' in value) || value['addWalletLater'] === undefined) return false;
    if (!('agio' in value) || value['agio'] === undefined) return false;
    if (!('canAbort' in value) || value['canAbort'] === undefined) return false;
    if (!('canAccept' in value) || value['canAccept'] === undefined) return false;
    if (!('canCancel' in value) || value['canCancel'] === undefined) return false;
    if (!('canIssue' in value) || value['canIssue'] === undefined) return false;
    if (!('canReceivePayment' in value) || value['canReceivePayment'] === undefined) return false;
    if (!('cancellationDeadline' in value) || value['cancellationDeadline'] === undefined) return false;
    if (!('considerationStartedAt' in value) || value['considerationStartedAt'] === undefined) return false;
    if (!('disagio' in value) || value['disagio'] === undefined) return false;
    if (!('frontendStatus' in value) || value['frontendStatus'] === undefined) return false;
    if (!('fulfilledAmount' in value) || value['fulfilledAmount'] === undefined) return false;
    if (!('investmentTotalFulfilled' in value) || value['investmentTotalFulfilled'] === undefined) return false;
    if (!('investmentTotal' in value) || value['investmentTotal'] === undefined) return false;
    if (!('investor' in value) || value['investor'] === undefined) return false;
    if (!('isEcsp' in value) || value['isEcsp'] === undefined) return false;
    if (!('isTokenized' in value) || value['isTokenized'] === undefined) return false;
    if (!('issuanceStatus' in value) || value['issuanceStatus'] === undefined) return false;
    if (!('kycStatus' in value) || value['kycStatus'] === undefined) return false;
    if (!('lastSettledPayout' in value) || value['lastSettledPayout'] === undefined) return false;
    if (!('maxAmount' in value) || value['maxAmount'] === undefined) return false;
    if (!('nextScheduledPayout' in value) || value['nextScheduledPayout'] === undefined) return false;
    if (!('paymentCode' in value) || value['paymentCode'] === undefined) return false;
    if (!('paymentDeadline' in value) || value['paymentDeadline'] === undefined) return false;
    if (!('paymentTotal' in value) || value['paymentTotal'] === undefined) return false;
    if (!('productName' in value) || value['productName'] === undefined) return false;
    if (!('redeemed' in value) || value['redeemed'] === undefined) return false;
    if (!('remainingAmount' in value) || value['remainingAmount'] === undefined) return false;
    if (!('signedAt' in value) || value['signedAt'] === undefined) return false;
    if (!('sumSettledPayouts' in value) || value['sumSettledPayouts'] === undefined) return false;
    if (!('tokenPrice' in value) || value['tokenPrice'] === undefined) return false;
    if (!('token' in value) || value['token'] === undefined) return false;
    if (!('usesEffecta' in value) || value['usesEffecta'] === undefined) return false;
    if (!('voucher' in value) || value['voucher'] === undefined) return false;
    if (!('wallet' in value) || value['wallet'] === undefined) return false;
    if (!('createdAt' in value) || value['createdAt'] === undefined) return false;
    if (!('exemptionOrderAmount' in value) || value['exemptionOrderAmount'] === undefined) return false;
    return true;
}

export function InvestmentDetailsFromJSON(json: any): InvestmentDetails {
    return InvestmentDetailsFromJSONTyped(json, false);
}

export function InvestmentDetailsFromJSONTyped(json: any, ignoreDiscriminator: boolean): InvestmentDetails {
    if (json == null) {
        return json;
    }
    return {
        
        'id': json['id'],
        'acceptedAt': json['accepted_at'] == null ? undefined : (new Date(json['accepted_at'])),
        'accruedInterest': MoneyFromJSON(json['accrued_interest']),
        'acquisitionType': json['acquisition_type'] == null ? undefined : InvestmentAcquisitionTypeRecipientEnumFromJSON(json['acquisition_type']),
        'addWalletLater': WalletTypeEnumFromJSON(json['add_wallet_later']),
        'agio': MoneyFromJSON(json['agio']),
        'canAbort': json['can_abort'],
        'canAccept': json['can_accept'],
        'canCancel': ((json['can_cancel'] as Array<any>).map(InvestmentCancellationEnumFromJSON)),
        'canIssue': json['can_issue'],
        'canReceivePayment': json['can_receive_payment'],
        'cancellationDate': json['cancellation_date'] == null ? undefined : (new Date(json['cancellation_date'])),
        'cancellationDeadline': (json['cancellation_deadline'] == null ? null : new Date(json['cancellation_deadline'])),
        'cancellationReason': json['cancellation_reason'] == null ? undefined : InvestmentCancellationEnumFromJSON(json['cancellation_reason']),
        'considerationStartedAt': (new Date(json['consideration_started_at'])),
        'disagio': MoneyFromJSON(json['disagio']),
        'frontendStatus': FrontendStatusEnumFromJSON(json['frontend_status']),
        'fulfilledAmount': json['fulfilled_amount'],
        'initiatedIdentification': json['initiated_identification'] == null ? undefined : json['initiated_identification'],
        'investmentTotalFulfilled': MoneyFromJSON(json['investment_total_fulfilled']),
        'investmentTotal': MoneyFromJSON(json['investment_total']),
        'investor': MinimalInvestorFromJSON(json['investor']),
        'isEcsp': json['is_ecsp'],
        'isTokenized': json['is_tokenized'],
        'issuanceStatus': TokenizationApiOperationStatusFromJSON(json['issuance_status']),
        'kycStatus': KycStatusEnumFromJSON(json['kyc_status']),
        'lastSettledPayout': MoneyFromJSON(json['last_settled_payout']),
        'maxAmount': json['max_amount'],
        'nextScheduledPayout': (json['next_scheduled_payout'] == null ? null : new Date(json['next_scheduled_payout'])),
        'paidAt': json['paid_at'] == null ? undefined : (new Date(json['paid_at'])),
        'paymentCode': json['payment_code'],
        'paymentDeadline': (json['payment_deadline'] == null ? null : new Date(json['payment_deadline'])),
        'paymentTotal': MoneyFromJSON(json['payment_total']),
        'productName': json['product_name'],
        'redeemed': json['redeemed'],
        'remainingAmount': json['remaining_amount'],
        'signedAt': (new Date(json['signed_at'])),
        'step': json['step'] == null ? undefined : StepEnumFromJSON(json['step']),
        'sumSettledPayouts': MoneyFromJSON(json['sum_settled_payouts']),
        'surveyValid': json['survey_valid'] == null ? undefined : json['survey_valid'],
        'surveyWaiverType': json['survey_waiver_type'] == null ? undefined : SurveyWaiverTypeEnumFromJSON(json['survey_waiver_type']),
        'tokenDeliveredAt': json['token_delivered_at'] == null ? undefined : (new Date(json['token_delivered_at'])),
        'tokenPrice': MoneyFromJSON(json['token_price']),
        'tokenTransactionHash': json['token_transaction_hash'] == null ? undefined : json['token_transaction_hash'],
        'token': InvestmentTokenForInvestorFromJSON(json['token']),
        'usesEffecta': json['uses_effecta'],
        'voucher': InvestmentVoucherWithDisagioFromJSON(json['voucher']),
        'wallet': WalletFromJSON(json['wallet']),
        'createdAt': (new Date(json['created_at'])),
        'exemptionOrderAmount': MoneyFromJSON(json['exemption_order_amount']),
        'exemptionOrderDate': json['exemption_order_date'] == null ? undefined : (new Date(json['exemption_order_date'])),
    };
}

export function InvestmentDetailsToJSON(value?: Omit<InvestmentDetails, 'id'|'accrued_interest'|'agio'|'can_abort'|'can_accept'|'can_cancel'|'can_issue'|'can_receive_payment'|'cancellation_deadline'|'consideration_started_at'|'disagio'|'frontend_status'|'investment_total_fulfilled'|'investment_total'|'is_ecsp'|'is_tokenized'|'issuance_status'|'kyc_status'|'last_settled_payout'|'next_scheduled_payout'|'payment_code'|'payment_deadline'|'payment_total'|'redeemed'|'sum_settled_payouts'|'token_price'|'token'|'uses_effecta'|'voucher'|'created_at'> | null): any {
    if (value == null) {
        return value;
    }
    return {
        
        'accepted_at': value['acceptedAt'] == null ? undefined : ((value['acceptedAt'] as any).toISOString()),
        'acquisition_type': InvestmentAcquisitionTypeRecipientEnumToJSON(value['acquisitionType']),
        'add_wallet_later': WalletTypeEnumToJSON(value['addWalletLater']),
        'cancellation_date': value['cancellationDate'] == null ? undefined : ((value['cancellationDate'] as any).toISOString()),
        'cancellation_reason': InvestmentCancellationEnumToJSON(value['cancellationReason']),
        'fulfilled_amount': value['fulfilledAmount'],
        'initiated_identification': value['initiatedIdentification'],
        'investor': MinimalInvestorToJSON(value['investor']),
        'max_amount': value['maxAmount'],
        'paid_at': value['paidAt'] == null ? undefined : ((value['paidAt'] as any).toISOString()),
        'product_name': value['productName'],
        'remaining_amount': value['remainingAmount'],
        'signed_at': ((value['signedAt']).toISOString()),
        'step': StepEnumToJSON(value['step']),
        'survey_valid': value['surveyValid'],
        'survey_waiver_type': SurveyWaiverTypeEnumToJSON(value['surveyWaiverType']),
        'token_delivered_at': value['tokenDeliveredAt'] == null ? undefined : ((value['tokenDeliveredAt'] as any).toISOString()),
        'token_transaction_hash': value['tokenTransactionHash'],
        'wallet': WalletToJSON(value['wallet']),
        'exemption_order_amount': MoneyToJSON(value['exemptionOrderAmount']),
        'exemption_order_date': value['exemptionOrderDate'] == null ? undefined : ((value['exemptionOrderDate'] as any).toISOString()),
    };
}

